import request from '@/request'
import type * as TYPE from './types'

/**
 * 平台后台分页查询客户（登录用户 tenantId 为空时使用）
 */
export const queryTenant = (params: {
  pageNo: number
  pageSize: number
  tenantName?: string
  tenantCode?: string
  tenantType?: string
  tree?: boolean
}): Promise<ResponseData<TYPE.queryTenantData>> => {
  return request.post('/tenant/query', params)
}

/**
 * 租户端全量列表：当前租户 + 子孙（登录用户有 tenantId 时使用，不分页）
 */
export const queryTenantList = (): Promise<ResponseData<TYPE.TenantItem[] | null>> => {
  return request.post('/tenant/queryTenantList')
}

export interface TenantTreeNode {
  tenantId: string | number
  tenantName: string
  parentId?: string | number | null
  children?: TenantTreeNode[]
}

/** 列表筛选用租户树：平台用户全量，租户用户仅会话权限范围 */
export const queryTenantTree = (): Promise<ResponseData<TenantTreeNode[] | null>> => {
  return request.post('/tenant/queryTenantTree')
}

/** 空数组不传，避免后台当成“查这些租户”。雪花 ID 必须保持字符串，Number() 会丢精度。 */
export const toRequestTenantIds = (
  ids?: Array<string | number> | null
): string[] | undefined => {
  if (!ids?.length) return undefined
  const strs = Array.from(
    new Set(ids.map((id) => String(id).trim()).filter((id) => id && id !== 'null' && id !== 'undefined'))
  )
  return strs.length ? strs : undefined
}

/**
 * 添加 / 更新客户
 */
export const queryTenantAdd = (params: TYPE.TenantInfoAddParams): Promise<ResponseData<object>> => {
  return request.post('/tenant/add', params)
}

/**
 * 删除客户
 */
export const delTenant = (params: {
  tenantId: string | number
}): Promise<ResponseData<object>> => {
  return request.get('/tenant/del', { params })
}

/**
 * 添加小区超管
 */
export const queryTenantAddAdmin = (params: {
  account: string
  password: string
  phone?: string
  roleId: string
  tenantId: string | number
  userName?: string
  userId?: string | number | null
}): Promise<ResponseData<object>> => {
  return request.post('/tenant/addAdmin', params)
}

/**
 * 查询小区超管
 */
export const queryTenantAdminInfo = (params: {
  tenantId: string | number
}): Promise<
  ResponseData<{
    account: string
    password: string
    userType: string
    phone: string
    userRole: {
      roleId: string
      roleName: string
    }
    userId: string
  }>
> => {
  return request.post('/tenant/queryAdmin', params)
}

/**
 * 查询小区详情信息
 */
export const queryTenantInfo = (params: {
  tenantId: string
}): Promise<ResponseData<TYPE.TenantInfoData[]>> => {
  return request.get('/tenant/queryTenantInfo', { params })
}

/**
 * 新增小区楼栋
 */
export const queryAddBuilding = (params: {
  tenantId: string
  buildingInfos: {
    tinfoName: string
    sort?: number
  }[]
}): Promise<ResponseData<object>> => {
  return request.post('/tenant/addBuilding', params)
}

/**
 * 新增小区楼层
 */
export const queryAddFloor = (params: {
  tenantId: string
  parentId: string
  floorInfos: {
    tinfoName: string
    sort?: number
  }[]
}): Promise<ResponseData<object>> => {
  return request.post('/tenant/addFloor', params)
}

/** 保存租户高德地图围栏与视角 */
export const saveTenantAmap = (params: {
  tenantId: string | number
  fence?: string
  lng?: number
  lat?: number
  zoom?: number
  pitch?: number
  rotation?: number
  /** 确认视角中心（GCJ-02）：lng/lat 为社居委位置，viewLng/viewLat 为镜头中心 */
  viewLng?: number
  viewLat?: number
}): Promise<ResponseData<object>> => {
  return request.post('/tenant/saveAmap', params)
}
